import { useCallback, useEffect, useState } from 'react';
import { TASKS } from '@visionqc/shared';
import { Sparkline } from '../components/charts/Sparkline';
import { RangeBar } from '../components/charts/RangeBar';

/**
 * How the models are actually behaving.
 *
 * EVERY FIGURE IS PER MODEL VERSION, because the average across them is the one
 * number that cannot be acted on. On the installation this was written against,
 * one model answers in 31ms and another in 371; one passes 93% of parts and
 * another passes 3.7% of the same kind. A single "avg latency" tile — which the
 * dashboard already has — turns both of those into 241ms and nothing.
 *
 * The widgets earn their place rather than decorate:
 *   - a shared-scale range bar, so speed is compared by length;
 *   - a sparkline, which says a direction a single number cannot;
 *   - a proportion bar where something genuinely is a share of a whole;
 *   - and status colour ONLY on verdicts, which is what it means here.
 *
 * No gauges. A gauge spends a quarter of a panel on one value, and five of them
 * side by side still leave you comparing needles.
 */

interface Model {
  version: string;
  task: 'anomaly' | 'classification';
  n: number;
  latency: { p50: number; p95: number; worst: number };
  verdicts: { good: number; bad: number; unsure: number };
  classification: { classified: number; declined: number; confidence: number | null };
  review: { reviewed: number; agreed: number; agreementRate: number } | null;
  firstSeen: string | null;
  lastSeen: string | null;
  spark: Array<{ day: string; p50: number }>;
}

interface Backtest {
  id: number;
  modelVersion: string;
  productName: string | null;
  sampled: number;
  changed: number;
  labelled: number;
  avgScore: number | null;
  createdAt: string | null;
}

interface Payload {
  window: string;
  includeDemo: boolean;
  excludedProducts: string[];
  models: Model[];
  backtests: Backtest[];
  confidence: Array<{ from: number; count: number }>;
}

const WINDOWS = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'all', label: 'Everything' },
];

export function ModelAnalytics() {
  const [window_, setWindow] = useState('30d');
  const [includeDemo, setIncludeDemo] = useState(false);
  const [data, setData] = useState<Payload | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    fetch(`/api/analytics/models?window=${window_}&includeDemo=${includeDemo}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Could not load model analytics'))))
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e: Error) => setError(e.message));
  }, [window_, includeDemo]);

  useEffect(() => load(), [load]);

  /*
    One scale across every row, so a bar's length means the same everywhere.

    Taken from models with enough observations to have a shape. A model that ran
    once has a p95 equal to its only measurement — on this installation that is
    1967ms against a real range of 31 to 560, and letting it set the axis
    squashes every model that actually ran into the left tenth. Rows below the
    threshold still draw, still at true length, and are dimmed and labelled so
    one observation is not read as a finding.
  */
  const ENOUGH = 10;
  const solid = (data?.models ?? []).filter((m) => m.n >= ENOUGH);
  const scaleMax = Math.max(1, ...(solid.length ? solid : (data?.models ?? [])).map((m) => m.latency.p95));

  return (
    <div>
      <h1 className="view-title">Model analytics</h1>

      <div className="panel" style={{ marginBottom: 20 }}>
        <div className="row" style={{ gap: 24, alignItems: 'flex-end' }}>
          <div>
            <label>Time window</label>
            <select value={window_} onChange={(e) => setWindow(e.target.value)}>
              {WINDOWS.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </select>
          </div>
          {/*
            Named on screen rather than filtered silently. A page that quietly
            drops a product is a page whose numbers nobody can reconcile against
            the database.
          */}
          <label className="row" style={{ gap: 8, marginBottom: 10, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={includeDemo}
              onChange={(e) => setIncludeDemo(e.target.checked)}
              style={{ width: 16, height: 16 }}
            />
            <span style={{ fontSize: 13, color: 'var(--text)' }}>Include demo data</span>
          </label>
          {data && !data.includeDemo && data.excludedProducts.length > 0 && (
            <span className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
              Leaving out {data.excludedProducts.join(', ')} — synthetic images, which flatter a
              model rather than test it.
            </span>
          )}
        </div>
      </div>

      {error && <div className="panel" style={{ color: 'var(--bad)' }}>{error}</div>}

      {!data ? (
        !error && <div className="panel spinner">Loading…</div>
      ) : data.models.length === 0 ? (
        <div className="panel muted">No inspections in this window.</div>
      ) : (
        <div className="grid" style={{ gap: 20 }}>
          <Headlines models={data.models} />

          <div className="panel">
            <strong>Speed, per model</strong>
            <p className="muted" style={{ fontSize: 12, margin: '4px 0 16px' }}>
              The mark is the typical part; the band reaches the 95th percentile — what the line has
              to keep up with. One scale across every row, so length is the comparison.
            </p>
            <div className="grid" style={{ gap: 14 }}>
              {data.models.map((m) => (
                <SpeedRow key={m.version} model={m} scaleMax={scaleMax} enough={ENOUGH} />
              ))}
            </div>
          </div>

          <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 20 }}>
            <div className="panel">
              <strong>What each model decides</strong>
              <p className="muted" style={{ fontSize: 12, margin: '4px 0 14px' }}>
                Status colour, used for status. Two models on the same kind of part can disagree
                completely — that shows here as two very different bars.
              </p>
              <div className="grid" style={{ gap: 12 }}>
                {data.models
                  .filter((m) => m.task === 'anomaly' && m.n > 0)
                  .map((m) => (
                    <VerdictRow key={m.version} model={m} enough={ENOUGH} />
                  ))}
              </div>
            </div>

            <div className="panel">
              <strong>Do people agree with it?</strong>
              <p className="muted" style={{ fontSize: 12, margin: '4px 0 14px' }}>
                Of the parts somebody re-judged by hand, how often they said the same thing. Always
                with its denominator: 5 out of 5 and 19 out of 60 are both &ldquo;agreement&rdquo;.
              </p>
              <div className="grid" style={{ gap: 12 }}>
                {data.models.map((m) => (
                  <AgreementRow key={m.version} model={m} />
                ))}
              </div>
            </div>
          </div>

          {data.confidence.length > 0 && <Confidence buckets={data.confidence} />}

          {data.backtests.length > 0 && <Backtests runs={data.backtests} />}
        </div>
      )}
    </div>
  );
}

/**
 * The three things worth knowing before scrolling.
 *
 * Chosen rather than computed from a list of every metric: the fastest and
 * slowest models bracket what the hardware has to do, and the worst agreement
 * rate is the only figure here that says a model might be wrong.
 */
function Headlines({ models }: { models: Model[] }) {
  const withSpeed = models.filter((m) => m.n > 2);
  const fastest = [...withSpeed].sort((a, b) => a.latency.p50 - b.latency.p50)[0];
  const slowest = [...withSpeed].sort((a, b) => b.latency.p50 - a.latency.p50)[0];
  const reviewed = models.filter((m) => m.review && m.review.reviewed >= 5);
  const worst = [...reviewed].sort(
    (a, b) => (a.review?.agreementRate ?? 1) - (b.review?.agreementRate ?? 1),
  )[0];
  const total = models.reduce((s, m) => s + m.n, 0);

  return (
    <div className="row" style={{ gap: 16, alignItems: 'stretch' }}>
      <div className="stat-tile" style={{ flex: '1 1 180px' }}>
        <div className="stat-label">Inspected</div>
        <div className="stat-value">{total.toLocaleString()}</div>
        <div className="muted" style={{ fontSize: 12 }}>
          across {models.length} model{models.length === 1 ? '' : 's'}
        </div>
      </div>
      {fastest && slowest && fastest.version !== slowest.version && (
        <div className="stat-tile" style={{ flex: '1 1 220px' }}>
          <div className="stat-label">Fastest vs slowest</div>
          <div className="stat-value" style={{ color: 'var(--accent)' }}>
            {Math.round(slowest.latency.p50 / Math.max(1, fastest.latency.p50))}×
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            {fastest.version} {fastest.latency.p50}ms · {slowest.version} {slowest.latency.p50}ms
          </div>
        </div>
      )}
      {worst?.review && (
        <div className="stat-tile" style={{ flex: '1 1 220px' }}>
          <div className="stat-label">Weakest agreement</div>
          <div
            className="stat-value"
            style={{ color: worst.review.agreementRate < 0.7 ? 'var(--bad)' : 'var(--good)' }}
          >
            {Math.round(worst.review.agreementRate * 100)}%
          </div>
          <div className="muted" style={{ fontSize: 12 }}>
            {worst.version} · {worst.review.reviewed} reviewed by hand
          </div>
        </div>
      )}
    </div>
  );
}

function SpeedRow({ model, scaleMax, enough }: { model: Model; scaleMax: number; enough: number }) {
  const thin = model.n < enough;
  return (
    // Dimmed, not hidden. A model that ran twice is still a fact; it is just
    // not evidence, and the eye should weight it accordingly.
    <div style={{ opacity: thin ? 0.55 : 1 }}>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 5, gap: 10 }}>
        <span className="row" style={{ gap: 8 }}>
          <strong style={{ fontSize: 14 }}>{model.version}</strong>
          <span className="muted" style={{ fontSize: 11 }}>{TASKS[model.task].label}</span>
        </span>
        <span className="row" style={{ gap: 12 }}>
          <Sparkline points={model.spark.map((s) => s.p50)} />
          <span style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>
            {model.latency.p50}
            <span className="muted"> / {model.latency.p95} ms</span>
          </span>
          <span
            className="muted"
            style={{ fontFamily: 'var(--mono)', fontSize: 12, width: 52, textAlign: 'right' }}
            title={thin ? 'Too few runs to read as a trend' : undefined}
          >
            {model.n.toLocaleString()}
            {thin && <span style={{ color: 'var(--warn)' }}> ⚠</span>}
          </span>
        </span>
      </div>
      <RangeBar p50={model.latency.p50} p95={model.latency.p95} scaleMax={scaleMax} />
    </div>
  );
}

function VerdictRow({ model, enough }: { model: Model; enough: number }) {
  const { good, bad, unsure } = model.verdicts;
  const total = good + bad + unsure || 1;
  /*
    A percentage from one inspection draws the same full-width bar as one from
    595, and reads as just as certain. The count sits beside it and the row is
    dimmed below the threshold — the bar is honest, the weight it carries should
    be too.
  */
  const thin = total < enough;
  const seg = (n: number, color: string, label: string) =>
    n === 0 ? null : (
      <div
        key={label}
        title={`${label} ${n.toLocaleString()}`}
        style={{ width: `${(n / total) * 100}%`, background: color, height: '100%' }}
      />
    );
  return (
    <div style={{ opacity: thin ? 0.55 : 1 }}>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 13 }}>{model.version}</span>
        <span className="muted" style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
          {thin ? `${good}/${total}` : `${Math.round((good / total) * 100)}% pass`}
          <span style={{ opacity: 0.7 }}> · {total.toLocaleString()}</span>
        </span>
      </div>
      {/* 2px surface gaps between segments, not strokes. */}
      <div
        className="row"
        style={{ gap: 2, height: 12, borderRadius: 6, overflow: 'hidden', flexWrap: 'nowrap' }}
      >
        {seg(good, 'var(--good)', 'GOOD')}
        {seg(unsure, 'var(--warn)', 'UNSURE')}
        {seg(bad, 'var(--bad)', 'BAD')}
      </div>
    </div>
  );
}

function AgreementRow({ model }: { model: Model }) {
  const r = model.review;
  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: 13 }}>{model.version}</span>
        <span className="muted" style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
          {r ? `${r.agreed} of ${r.reviewed}` : 'nobody has checked'}
        </span>
      </div>
      {r ? (
        <div style={{ height: 12, borderRadius: 6, background: 'var(--bg)', overflow: 'hidden' }}>
          <div
            style={{
              width: `${r.agreementRate * 100}%`,
              height: '100%',
              /*
                Status colour, and it is status: a model people keep overruling
                is a failing model. The threshold is deliberately generous —
                below 70% it is red, because at that point the model is wrong
                more often than a coin would be surprised by.
              */
              background: r.agreementRate >= 0.9
                ? 'var(--good)'
                : r.agreementRate >= 0.7
                  ? 'var(--warn)'
                  : 'var(--bad)',
              borderRadius: 6,
              transition: 'width 0.4s ease',
            }}
          />
        </div>
      ) : (
        <div
          style={{
            height: 12,
            borderRadius: 6,
            /* Hatched, not empty: unknown is not zero. */
            background:
              'repeating-linear-gradient(45deg, var(--bg) 0 6px, var(--bg-panel-2) 6px 12px)',
          }}
        />
      )}
    </div>
  );
}

function Confidence({ buckets }: { buckets: Array<{ from: number; count: number }> }) {
  const max = Math.max(...buckets.map((b) => b.count), 1);
  return (
    <div className="panel">
      <strong>How sure the classifier is</strong>
      <p className="muted" style={{ fontSize: 12, margin: '4px 0 14px' }}>
        Confidence of every prediction, in tenths. A model piled up at the low end is guessing;
        one piled at the top may be overconfident rather than right.
      </p>
      <div className="row" style={{ gap: 4, alignItems: 'flex-end', height: 120 }}>
        {Array.from({ length: 10 }, (_, i) => {
          const b = buckets.find((x) => Math.round(x.from * 10) === i);
          const n = b?.count ?? 0;
          return (
            <div key={i} style={{ flex: 1, textAlign: 'center' }} title={`${i * 10}–${i * 10 + 10}%: ${n}`}>
              <div
                style={{
                  height: `${(n / max) * 90}px`,
                  minHeight: n > 0 ? 3 : 0,
                  background: 'var(--accent)',
                  borderRadius: '4px 4px 0 0',
                  transition: 'height 0.4s ease',
                }}
              />
              <div className="muted" style={{ fontSize: 10, marginTop: 4 }}>
                {i * 10}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Backtests({ runs }: { runs: Backtest[] }) {
  return (
    <div className="panel">
      <strong>Backtests</strong>
      <p className="muted" style={{ fontSize: 12, margin: '4px 0 14px' }}>
        A candidate model re-run over images already inspected. &ldquo;Changed&rdquo; is how many
        verdicts it would flip — with nothing labelled, that number says a model is different, not
        that it is better.
      </p>
      <table>
        <thead>
          <tr>
            <th>Model</th>
            <th>Product</th>
            <th>Sampled</th>
            <th>Would change</th>
            <th>Labelled</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((b) => {
            const share = b.sampled > 0 ? b.changed / b.sampled : 0;
            return (
              <tr key={b.id} style={{ cursor: 'default' }}>
                <td style={{ fontSize: 13 }}>{b.modelVersion}</td>
                <td className="muted" style={{ fontSize: 13 }}>{b.productName ?? '—'}</td>
                <td style={{ fontFamily: 'var(--mono)', fontSize: 13 }}>{b.sampled}</td>
                <td>
                  <div className="row" style={{ gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 70, height: 8, background: 'var(--bg)', borderRadius: 4 }}>
                      <div
                        style={{
                          width: `${share * 100}%`,
                          height: '100%',
                          background: share > 0.5 ? 'var(--warn)' : 'var(--accent)',
                          borderRadius: 4,
                        }}
                      />
                    </div>
                    <span style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
                      {Math.round(share * 100)}%
                    </span>
                  </div>
                </td>
                <td
                  style={{
                    fontFamily: 'var(--mono)',
                    fontSize: 13,
                    color: b.labelled === 0 ? 'var(--warn)' : undefined,
                  }}
                  title={b.labelled === 0 ? 'Nothing to compare against — this says different, not better' : undefined}
                >
                  {b.labelled}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
